import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { CartService } from '../../../shared/services/cart.service';
import { ShopService } from '../../../shared/services/shop.service';
import { ShopProduct } from '../../../shared/interfaces/shop-product.interface';
import { Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { StripeService } from '../../../shared/services/stripe.service';
import { isPlatformBrowser } from '@angular/common';
import { CheckoutService } from '../../../shared/services/checkout.service';
import { ReferralTrackingService } from '../../../shared/services/referral-tracking.service';
import { MailchimpService } from '../../../services/mailchimp.service';
import { PaymentScheduleDialogComponent } from './payment-schedule-dialog/payment-schedule-dialog.component';
@Component({
    standalone: true,
    selector: 'app-checkout',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatSelectModule,
        MatIconModule,
        MatDialogModule,
        RouterModule,
    ],
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit, OnDestroy {
  checkoutForm: FormGroup;
  cartProducts: ShopProduct[] = [];
  monthlyTotal = 0;
  fullTotal = 0;
  isProcessing = false;
  paymentError: string = '';
  private subscription = new Subscription();
  selectedPaymentPlan = 24;
  paymentPlans = [
    { months: 30, label: '30 Monatsraten', key: 'thirtyMonths' },
    { months: 24, label: '24 Monatsraten', key: 'twentyFourMonths' },
    { months: 18, label: '18 Monatsraten', key: 'eighteenMonths' },
    { months: 12, label: '12 Monatsraten', key: 'twelveMonths' },
    { months: 6, label: '6 Monatsraten', key: 'sixMonths' },
    { months: 0, label: 'Einmalzahlung', key: 'fullPayment' }
  ];
  paymentStartDates: { value: number; label: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private cartService: CartService,
    private shopService: ShopService,
    private dialog: MatDialog,
    private stripeService: StripeService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    public checkoutService: CheckoutService,
    private referralTrackingService: ReferralTrackingService,
    private snackBar: MatSnackBar,
    private mailchimpService: MailchimpService
  ) {
    this.checkoutForm = this.createForm();
    this.setupFormSubscriptions();
    this.initializeFormWithDummyData();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      street: ['', [Validators.required, Validators.minLength(2)]],
      streetNumber: ['', [Validators.required]],
      zipCode: ['', [Validators.required, Validators.pattern('^[0-9]{4,5}$')]],
      city: ['', [Validators.required, Validators.minLength(2)]],
      country: ['AT', [Validators.required]],
      mobile: ['', [Validators.required, Validators.pattern('^[+]?[0-9\\s-]{8,}$')]],
      paymentPlan: [24],
      paymentStartDate: [0],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptWithdrawalWaiver: [false, [Validators.requiredTrue]],
      acceptPaymentDate: [false], // Validator will be set dynamically based on payment plan
      newsletter: [false],
      becomePartner: [false]
    });
  }

  private setupFormSubscriptions() {
    this.subscription.add(
      this.checkoutForm.get('country')?.valueChanges.subscribe(country => {
        if (country) {
          this.checkoutService.setSelectedCountry(country);
          this.calculateTotals();
        }
      })
    );

    this.subscription.add(
      this.checkoutForm.get('paymentPlan')?.valueChanges.subscribe(paymentPlan => {
        this.calculateTotals();
        // Only update payment start dates, don't reset the selected value
        this.updatePaymentStartDates();
        // Update acceptPaymentDate validator based on payment plan
        this.updateAcceptPaymentDateValidator(paymentPlan);
      })
    );
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      await this.loadCartProducts();
      this.calculateTotals();
      this.updatePaymentStartDates(true);
      // Initialize validator for current payment plan
      this.updateAcceptPaymentDateValidator(this.checkoutForm.get('paymentPlan')?.value);
    }
  }

  private updateAcceptPaymentDateValidator(paymentPlan: number) {
    const acceptPaymentDateControl = this.checkoutForm.get('acceptPaymentDate');
    if (acceptPaymentDateControl) {
      if (paymentPlan > 0) {
        // For installment plans, the checkbox is required
        acceptPaymentDateControl.setValidators([Validators.requiredTrue]);
        acceptPaymentDateControl.setValue(false); // Reset to false so user must check it
      } else {
        // For one-time payments, no validation needed (checkbox is hidden)
        acceptPaymentDateControl.clearValidators();
        acceptPaymentDateControl.setValue(true); // Set to true since it's not needed
      }
      acceptPaymentDateControl.updateValueAndValidity();
    }
  }

  private initializeFormWithDummyData() {
    if (!environment.production) {
      this.checkoutForm.patchValue({
        firstName: 'David',
        lastName: 'Velickovic',
        email: 'davidvelickovic@hotmail.com',
        street: 'Aumühlstraße',
        streetNumber: '36',
        zipCode: '4050',
        city: 'Traun',
        country: 'AT',
        mobile: '+436606619150'
      });
    }
  }

  private async loadCartProducts() {
    try {
      const cartItems = Array.from(this.cartService.getCartItems());
      const products = await firstValueFrom(this.shopService.getAllProducts());
      
      if (products) {
        this.cartProducts = products.filter(product => 
          cartItems.includes(product.id)
        );

        if (this.cartProducts.length === 0) {
          this.router.navigate(['/shop']);
          return;
        }

        this.calculateTotals();
      }
    } catch (error) {
      console.error('Error loading cart products:', error);
      this.snackBar.open('Fehler beim Laden der Produkte', 'Schließen', { duration: 5000 });
    }
  }

  private calculateTotals() {
    const baseTotal = this.cartProducts.reduce((sum, product) => sum + product.price, 0);
    this.fullTotal = baseTotal;
    
    const selectedPlan = this.checkoutForm.get('paymentPlan')?.value;
    if (selectedPlan === 0) {
      this.monthlyTotal = 0;
    } else {
      this.monthlyTotal = this.fullTotal / selectedPlan;
    }
  }

  private getStripePriceId(months: number): string {
    const product = this.cartProducts[0];
    if (!product || !product.stripePriceIds) {
      throw new Error('Kein gültiges Produkt oder Preise gefunden');
    }

    const plan = this.paymentPlans.find(p => p.months === months);
    if (!plan) {
      throw new Error('Ungültiger Zahlungsplan');
    }

    const priceId = product.stripePriceIds[plan.key as keyof typeof product.stripePriceIds];
    if (!priceId) {
      throw new Error(`Kein Preis gefunden für Plan: ${plan.label}`);
    }

    return priceId;
  }

  async onSubmit() {
    if (this.isProcessing) return;

    if (!this.checkoutForm.valid) {
      this.markFormGroupTouched(this.checkoutForm);
      this.snackBar.open('Bitte füllen Sie alle erforderlichen Felder aus', 'Schließen', { duration: 5000 });
      return;
    }

    if (!this.checkoutForm.get('acceptTerms')?.value) {
      this.paymentError = 'Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen und Datenschutzerklärung.';
      this.snackBar.open(this.paymentError, 'Schließen', { duration: 5000 });
      return;
    }

    if (!this.checkoutForm.get('acceptWithdrawalWaiver')?.value) {
      this.paymentError = 'Bitte stimmen Sie dem Verzicht auf das Widerrufsrecht zu.';
      this.snackBar.open(this.paymentError, 'Schließen', { duration: 5000 });
      return;
    }

    const paymentPlan = this.checkoutForm.get('paymentPlan')?.value;
    if (paymentPlan > 0 && !this.checkoutForm.get('acceptPaymentDate')?.value) {
      this.paymentError = 'Bitte bestätigen Sie das Datum der ersten Zahlung.';
      this.snackBar.open(this.paymentError, 'Schließen', { duration: 5000 });
      return;
    }

    this.isProcessing = true;
    this.paymentError = '';

    try {
      const formValues = this.checkoutForm.value;
      const paymentPlan = formValues.paymentPlan;
      const priceId = this.getStripePriceId(paymentPlan);

      // Handle newsletter subscription if checked
      if (formValues.newsletter) {
        await this.subscribeToNewsletter(
          formValues.email,
          formValues.firstName,
          formValues.lastName
        );
      }

      // Get current referral code if active
      console.log('=== CHECKOUT DEBUG START ===');
      console.log('Referral tracking service:', this.referralTrackingService);
      console.log('Service exists:', !!this.referralTrackingService);
      
      const currentReferral = this.referralTrackingService.getCurrentReferral();
      console.log('Current referral during checkout:', currentReferral);
      console.log('Referral code extracted:', currentReferral?.referralCode);
      console.log('Has active referral:', this.referralTrackingService.hasActiveReferral());
      
      // Double check localStorage directly
      const rawStorage = localStorage.getItem('hn_referral');
      console.log('Raw localStorage hn_referral:', rawStorage);
      if (rawStorage) {
        try {
          const parsed = JSON.parse(rawStorage);
          console.log('Parsed localStorage referral:', parsed);
        } catch (e) {
          console.log('Failed to parse localStorage referral:', e);
        }
      }
      console.log('=== CHECKOUT DEBUG END ===');
      
      // Get referral code with fallback to localStorage
      let referralCode = currentReferral?.referralCode;
      if (!referralCode) {
        // Fallback: try to get directly from localStorage
        try {
          const storedData = localStorage.getItem('hn_referral');
          if (storedData) {
            const { session } = JSON.parse(storedData);
            referralCode = session?.referralCode;
            console.log('Fallback: Got referral code from localStorage:', referralCode);
          }
        } catch (e) {
          console.error('Fallback: Failed to get referral from localStorage:', e);
        }
      }
      
      console.log('Final referral code to be sent:', referralCode);
      
      console.log('✅ REFERRAL CODE FOUND:', referralCode || 'NONE');
      
      const customerData = {
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        street: formValues.street,
        streetNumber: formValues.streetNumber,
        zipCode: formValues.zipCode,
        city: formValues.city,
        country: formValues.country,
        mobile: formValues.mobile,
        paymentPlan: paymentPlan,
        paymentStartDate: paymentPlan > 0 && formValues.paymentStartDate !== 0 ? formValues.paymentStartDate : undefined,
        newsletter: formValues.newsletter,
        becomePartner: formValues.becomePartner,
        newsletterSubscribed: formValues.newsletter,
        referralCode: referralCode || undefined
      };

      console.log('Creating checkout session with:', {
        priceId,
        customerData,
        referralCodeInCustomerData: customerData.referralCode,
        hasReferralCode: !!customerData.referralCode,
        paymentStartDateDebug: {
          rawValue: formValues.paymentStartDate,
          isImmediate: formValues.paymentStartDate === 0,
          timestamp: formValues.paymentStartDate === 0 ? Math.floor(Date.now() / 1000) : formValues.paymentStartDate
        }
      });
      
      // Referral code should now be included in the API call

      const response = await firstValueFrom(
        this.stripeService.createCheckoutSession(priceId, customerData)
      );

      if (!response?.url) {
        throw new Error('Keine Checkout-URL erhalten');
      }

      // Store acceptance timestamp
      localStorage.setItem('terms_accepted', new Date().toISOString());
      
      // Clear cart after successful checkout creation
      this.cartService.clearCart();
      
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      console.error('Checkout error:', error);
      this.handleError(error);
    } finally {
      this.isProcessing = false;
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private handleError(error: any) {
    let message = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
    
    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    }

    this.paymentError = message;
    this.snackBar.open(message, 'Schließen', { duration: 5000 });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.checkoutForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;
    if (errors['required']) return 'Dieses Feld ist erforderlich';
    if (errors['email']) return 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    if (errors['minlength']) return `Mindestens ${errors['minlength'].requiredLength} Zeichen erforderlich`;
    if (errors['pattern']) {
      switch (fieldName) {
        case 'mobile':
          return 'Bitte geben Sie eine gültige Telefonnummer ein';
        case 'zipCode':
          return 'Bitte geben Sie eine gültige Postleitzahl ein';
        default:
          return 'Ungültiges Format';
      }
    }
    return '';
  }

  openPaymentSchedule() {
    const selectedPlan = this.checkoutForm.get('paymentPlan')?.value;
    if (!selectedPlan) return;

    this.dialog.open(PaymentScheduleDialogComponent, {
      width: '500px',
      data: {
        monthlyAmount: this.monthlyTotal,
        totalAmount: this.fullTotal,
        months: selectedPlan,
        paymentStartDate: this.checkoutForm.get('paymentStartDate')?.value
      }
    });
  }

  hasHorizonAcademy(): boolean {
    return this.cartProducts.some(product => 
      product.name.toLowerCase().includes('horizon academy')
    );
  }

  onPaymentPlanChange(months: number) {
    this.checkoutForm.get('paymentPlan')?.setValue(months);
    this.calculateTotals();
  }

  async subscribeToNewsletter(email: string, firstName?: string, lastName?: string) {
    const mergeFields: Record<string, string> = {
      FNAME: firstName || '',
      LNAME: lastName || ''
    };

    try {
      await firstValueFrom(this.mailchimpService.addToList(email, mergeFields));
      this.snackBar.open('Erfolgreich zum Newsletter angemeldet!', 'Schließen', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
    } catch (error: unknown) {
      console.error('Error subscribing to newsletter:', error);
      this.snackBar.open('Newsletter-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Schließen', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  private updatePaymentStartDates(setDefaultValue: boolean = false) {
    // Get current date in user's timezone and set to start of day
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates: { value: number; label: string }[] = [];
    
    // Helper function to format date label in German
    const formatGermanDate = (date: Date) => {
      return `${date.getDate()}. ${date.toLocaleString('de-DE', { month: 'long' })}`;
    };

    // Helper function to check if date is at least 3 days in the future
    const isValidFutureDate = (date: Date) => {
      const threeDaysFromNow = new Date(today);
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
      threeDaysFromNow.setHours(0, 0, 0, 0);
      return date.getTime() >= threeDaysFromNow.getTime();
    };

    // Helper function to get next valid date for a specific day of month
    const getNextValidDate = (dayOfMonth: number) => {
      let targetDate = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);
      targetDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      
      // If date isn't valid, move to next month
      while (!isValidFutureDate(targetDate)) {
        targetDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, dayOfMonth);
        targetDate.setHours(12, 0, 0, 0);
      }
      
      return targetDate;
    };

    // Helper function to convert date to Unix timestamp (in seconds)
    const dateToUnixTimestamp = (date: Date) => {
      // Add 12 hours to ensure we're well into the day regardless of timezone
      const timestamp = new Date(date);
      timestamp.setHours(12, 0, 0, 0);
      return Math.floor(timestamp.getTime() / 1000);
    };

    // Define the days of the month for payment dates
    const paymentDays = [3, 6, 9, 12, 15, 18, 21, 25, 28];
    
    // Add regular payment days
    paymentDays.forEach(day => {
      const date = getNextValidDate(day);
      dates.push({
        value: dateToUnixTimestamp(date),
        label: formatGermanDate(date)
      });
    });

    // Add end of month (30th or 31st depending on the month)
    const lastDayDate = getNextValidDate(31);
    // If the date rolled over to the next month, use 30th instead
    if (lastDayDate.getDate() !== 31) {
      const thirtiethDate = getNextValidDate(30);
      dates.push({
        value: dateToUnixTimestamp(thirtiethDate),
        label: formatGermanDate(thirtiethDate)
      });
    } else {
      dates.push({
        value: dateToUnixTimestamp(lastDayDate),
        label: formatGermanDate(lastDayDate)
      });
    }

    // Sort dates chronologically
    this.paymentStartDates = dates.sort((a, b) => a.value - b.value);
    
    // Set default value to the earliest date only if explicitly requested and no value is set
    if (setDefaultValue) {
      const currentValue = this.checkoutForm.get('paymentStartDate')?.value;
      if (this.paymentStartDates.length > 0 && (currentValue === null || currentValue === undefined)) {
        this.checkoutForm.get('paymentStartDate')?.setValue(this.paymentStartDates[0].value);
      }
    }

    // Log dates for debugging with more detail
    console.log('Date calculations:', {
      currentTime: new Date().toISOString(),
      currentTimeLocal: new Date().toLocaleString('de-DE'),
      minimumDate: new Date(today.getTime() + (3 * 24 * 60 * 60 * 1000)).toLocaleString('de-DE'),
      availableDates: this.paymentStartDates.map(d => ({
        date: new Date(d.value * 1000).toISOString(),
        localDate: new Date(d.value * 1000).toLocaleString('de-DE'),
        timestamp: d.value,
        label: d.label,
        daysFromNow: Math.floor((d.value * 1000 - today.getTime()) / (24 * 60 * 60 * 1000))
      }))
    });
  }

  getSelectedPaymentDate(): Date | null {
    const timestamp = this.checkoutForm.get('paymentStartDate')?.value;
    if (timestamp === 0) {
      return new Date(); // Return today's date for immediate payment
    }
    return timestamp ? new Date(timestamp * 1000) : null;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
} 
